import { z } from 'zod';

export type RatesStatus = 'LOADING' | 'SUCCESS' | 'ERROR' | 'STALE';

export interface RatesState {
  status: RatesStatus;
  rates: Record<string, number> | null;
  updatedAt: string | null;
  base: 'USDT';
}

const RATES_STORAGE_KEY = 'playerso:rates-usdt';
const FRESH_MS = 24 * 60 * 60 * 1000;
const FETCH_TIMEOUT_MS = 8000;

interface RatesUrl {
  url: string;
  baseKey: 'usdt' | 'usd';
}

const RATES_URLS: RatesUrl[] = [
  {
    url: 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usdt.min.json',
    baseKey: 'usdt',
  },
  {
    url: 'https://latest.currency-api.pages.dev/v1/currencies/usdt.min.json',
    baseKey: 'usdt',
  },
  {
    url: 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.min.json',
    baseKey: 'usd',
  },
  {
    url: 'https://latest.currency-api.pages.dev/v1/currencies/usd.min.json',
    baseKey: 'usd',
  },
];

const ratesPayloadSchema = z.object({
  date: z.string(),
  usdt: z.record(z.string(), z.number()).optional(),
  usd: z.record(z.string(), z.number()).optional(),
});

interface RatesCache {
  date: string;
  rates: Record<string, number>;
  savedAt: string;
}

function readCache(): RatesCache | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(RATES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RatesCache>;
    if (typeof parsed.date !== 'string') return null;
    if (typeof parsed.savedAt !== 'string') return null;
    if (typeof parsed.rates !== 'object' || parsed.rates === null) return null;
    const rates: Record<string, number> = {};
    for (const [k, v] of Object.entries(parsed.rates)) {
      if (typeof v === 'number' && Number.isFinite(v)) rates[k.toLowerCase()] = v;
    }
    return { date: parsed.date, rates, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

function writeCache(cache: RatesCache): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(RATES_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    /* storage unavailable — rates still served in-memory */
  }
}

function isFresh(savedAt: string): boolean {
  const ts = Date.parse(savedAt);
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts < FRESH_MS;
}

async function fetchWithTimeout(url: string): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

function normalizeRates(raw: Record<string, number>): Record<string, number> {
  const rates: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === 'number' && Number.isFinite(v)) rates[k.toLowerCase()] = v;
  }
  return rates;
}

/**
 * Sync hydrate from localStorage. Fresh (<24h) -> SUCCESS with no fetch;
 * expired -> STALE (keeps old rates; caller refetches); none -> LOADING.
 * Never throws.
 */
export function getInitialRatesState(): RatesState {
  try {
    const cached = readCache();
    if (!cached) return { status: 'LOADING', rates: null, updatedAt: null, base: 'USDT' };
    if (isFresh(cached.savedAt)) {
      return { status: 'SUCCESS', rates: cached.rates, updatedAt: cached.date, base: 'USDT' };
    }
    return { status: 'STALE', rates: cached.rates, updatedAt: cached.date, base: 'USDT' };
  } catch {
    return { status: 'LOADING', rates: null, updatedAt: null, base: 'USDT' };
  }
}

let inflight: Promise<RatesState> | null = null;

async function fetchFreshRates(): Promise<RatesState> {
  const cached = readCache();
  const staleFallback: RatesState = cached
    ? { status: 'STALE', rates: cached.rates, updatedAt: cached.date, base: 'USDT' }
    : { status: 'ERROR', rates: null, updatedAt: null, base: 'USDT' };
  try {
    for (const { url, baseKey } of RATES_URLS) {
      try {
        const res = await fetchWithTimeout(url);
        if (!res.ok) continue;
        const json: unknown = await res.json();
        const parsed = ratesPayloadSchema.safeParse(json);
        if (!parsed.success) continue;
        const table = parsed.data[baseKey];
        if (!table) continue;
        const rates = normalizeRates(table);
        if (Object.keys(rates).length === 0) continue;
        const date = parsed.data.date;
        writeCache({ date, rates, savedAt: new Date().toISOString() });
        return { status: 'SUCCESS', rates, updatedAt: date, base: 'USDT' };
      } catch {
        continue;
      }
    }
  } catch {
    /* fall through to stale/error fallback */
  }
  return staleFallback;
}

/**
 * Fetch rates with module-level single-flight: concurrent callers share one
 * request. USDT feed first; USD feed as an approx-par fallback. Never throws —
 * failure yields ERROR (or STALE when an expired cache exists).
 */
export function refreshRates(): Promise<RatesState> {
  if (!inflight) {
    inflight = fetchFreshRates().finally(() => {
      inflight = null;
    });
  }
  return inflight;
}
